import { PolymerElement, html } from "@polymer/polymer/polymer-element.js";
import { afterNextRender } from "@polymer/polymer/lib/utils/render-status.js";
import "@polymer/polymer/lib/elements/dom-if.js";
import "@polymer/polymer/lib/elements/dom-repeat.js";
import "@polymer/iron-ajax";

import iconStars from "../../../images/icons/icon_stars.svg";
import {
  CHAT_CREDIT_DECREASE_AFTER_SUBMIT,
  buildAiChatSseUrl,
  convertMarkdownToHtml,
  mapApiChatToTurn,
  parseChatCreditLimit,
  syncChatCredit,
} from "../../../utils/ai";

import "../../../components/desktop/ai/PromptInputBox/PromptInputBox";
import "../../../components/desktop/ai/LimitUsageBanner/LimitUsageBanner";
import "../../../components/desktop/ai/LimitReachedModal/LimitReachedModal";
import "../../../components/desktop/ai/AddLimitModal/AddLimitModal";
import "../../../components/desktop/ai/LimitedAccessComposer/LimitedAccessComposer";
import "../../../components/desktop/ai/ErrorDisplay/ErrorDisplay";
import "../../../components/desktop/shared/GeneralToast";
import "../../../components/desktop/ai/TopicSidebar/TopicSidebar";
import "../../../components/desktop/ai/ChatBubble/ChatBubble";
import "../../../components/desktop/ai/LoadingDisplay/LoadingDisplay";

const AI_LANDING_PATH = "/ai";
const PENDING_QUESTION_TURN_ID = "pending-question";
const PENDING_ANSWER_TURN_ID = "pending-answer";
const DEFAULT_STREAM_ERROR_MESSAGE = "Terjadi kendala. Silakan coba lagi.";

class AIPage extends PolymerElement {
  static get template() {
    return html`
      <style>
        :host {
          display: block;
          width: 100%;
          min-height: calc(100vh - 56px);
          box-sizing: border-box;
          --ai-page-top-offset: 56px;
          --ai-composer-height: 160px;
        }

        .ai-page__shell {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          width: 100%;
          min-height: calc(100vh - 56px);
          box-sizing: border-box;
        }

        .ai-page__sidebar {
          flex-shrink: 0;
          align-self: flex-start;
          position: sticky;
          top: 0;
          height: calc(100vh - 56px);
          max-height: calc(100vh - 56px);
          box-sizing: border-box;
        }

        .ai-page__main {
          flex: 1 1 auto;
          min-width: 0;
          width: 100%;
          display: flex;
          flex-direction: column;
          overflow: visible;
          box-sizing: border-box;
          padding: 0 32px 0;
        }

        .ai-page__main .ai-landing {
          flex: 1 1 auto;
          width: 100%;
          display: block;
          overflow: visible;
        }

        @media (max-width: 720px) {
          .ai-page__sidebar {
            display: none !important;
          }
          .ai-page__main {
            padding-left: 16px;
            padding-right: 16px;
          }
          .ai-prompt-stack__composer {
            left: 0;
            padding-left: 16px;
            padding-right: 16px;
          }
        }

        /* --- Prompt stack: landing mode (centered) vs chat mode (full-width) --- */

        .ai-prompt-stack {
          width: 100%;
          max-width: 666.5px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          box-sizing: border-box;
        }

        .ai-prompt-stack--chatting {
          max-width: none;
          margin: 0;
          padding-bottom: var(--ai-composer-height, 160px);
        }

        .ai-prompt-stack__scroll {
          display: block;
          width: 100%;
          overflow: visible;
        }
        /* Chat mode: scroll transcript from top (history / idle). */
        .ai-prompt-stack--chatting .ai-prompt-stack__scroll {
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          max-height: calc(
            100vh - var(--ai-page-top-offset, 56px) - var(--ai-composer-height, 160px)
          );
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .ai-prompt-stack__composer {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 32px 20px;
          background-color: #ffffff;
        }

        .ai-prompt-stack--chatting .ai-prompt-stack__composer {
          position: fixed;
          bottom: 0;
          left: 338px;
          right: 0;
          z-index: 97;
          width: auto;
          box-shadow: none;
          border-top: none;
        }

        /* --- Transcript container --- */

        .ai-transcript {
          display: flex;
          flex-direction: column;
          width: 100%;
          align-items: stretch;
          padding-bottom: var(--ai-transcript-bottom-space, 0px);
        }

        /* Extra bottom space so the latest question can scroll up under the header. */
        :host([loading-on-submit]) .ai-transcript {
          padding-bottom: calc(
            100vh - var(--ai-page-top-offset, 56px) - var(--ai-composer-height, 160px)
          );
        }

        .ai-transcript__older-sentinel {
          height: 1px;
          width: 100%;
          flex-shrink: 0;
          pointer-events: none;
        }

        .ai-transcript__older-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 8px 0 4px;
          min-height: 36px;
        }

        .ai-transcript__older-spinner {
          width: 20px;
          height: 20px;
          box-sizing: border-box;
          border: 2px solid #e0e0e0;
          border-top-color: #3570d2;
          border-radius: 50%;
          animation: ai-older-spin 0.65s linear infinite;
        }

        @keyframes ai-older-spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ai-transcript__older-spinner {
            animation: none;
            border-top-color: #e0e0e0;
          }
        }

        .ai-transcript__older-loading-text {
          font-size: 13px;
          line-height: 20px;
          color: #7f7f7f;
          font-family: Lato, sans-serif;
        }

        /* --- Skeleton loading animation --- */

        .ai-transcript__loading-head {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 9px;
        }

        .ai-transcript__loading-head svg {
          flex-shrink: 0;
        }

        .ai-transcript__loading-copy {
          margin: 0;
          font-family: "Lato", sans-serif;
          font-style: italic;
          font-weight: 400;
          font-size: 14px;
          line-height: 26px;
          color: #7f7f7f;
        }

        .ai-transcript__skel-rows {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }

        .ai-transcript__skel-line {
          display: flex;
          gap: 8px;
          align-items: center;
          width: 100%;
        }

        @keyframes ai-transcript-skel-shimmer {
          0% {
            background-position: 120% 0;
          }
          100% {
            background-position: -20% 0;
          }
        }

        .ai-transcript__skel-pill {
          height: 22px;
          border-radius: 6px;
          flex: 1 1 auto;
          min-width: 48px;
          background: linear-gradient(
            90deg,
            #f2f2f2 0%,
            #f2f2f2 35%,
            #fefefe 50%,
            #f2f2f2 65%,
            #f2f2f2 100%
          );
          background-size: 200% 100%;
          animation: ai-transcript-skel-shimmer 1.35s ease-in-out infinite;
        }

        .ai-transcript__skel-line .ai-transcript__skel-pill:nth-child(2) {
          animation-delay: 0.12s;
        }

        .ai-transcript__skel-line .ai-transcript__skel-pill:nth-child(3) {
          animation-delay: 0.24s;
        }

        @media (prefers-reduced-motion: reduce) {
          .ai-transcript__skel-pill {
            animation: none;
            background: linear-gradient(90deg, #fefefe 0%, #f2f2f2 100%);
          }
        }

        .ai-transcript__skel-pill--fixed148 {
          flex: 0 0 148px;
          width: 148px;
        }

        .ai-transcript__skel-pill--fixed215 {
          flex: 0 0 215px;
          width: 215px;
        }

        .ai-transcript__skel-pill--fixed239 {
          flex: 0 0 239px;
          width: 239px;
        }

        /* --- Composer area --- */

        .ai-composer {
          width: 100%;
          max-width: 666.5px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .ai-banner-container {
          border: 1px solid #d9d9d9;
          box-shadow: 0px 1px 10px rgba(66, 66, 66, 0.15);
          background-color: #ECECEC;
          border-bottom-left-radius: 20px;
          border-bottom-right-radius: 20px;
          border-top-left-radius: 14px;
          border-top-right-radius: 14px;
        }

        .ai-composer .ai-composer-disclaimer {
          margin-top: 12px;
        }

        .ai-composer-disclaimer {
          margin: 0;
          font-family: "Lato", sans-serif;
          font-weight: 400;
          font-size: 14px;
          line-height: 1.5;
          color: #7f7f7f;
          text-align: center;
          width: 100%;
        }

        /* --- Landing: layout, welcome header, suggestion chips, prompt slot --- */

        .ai-landing {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          padding: clamp(72px, 18vh, 250px) 16px 24px;
          box-sizing: border-box;
          overflow: visible;
        }

        .ai-landing--chatting {
          padding: 12px 0 0;
        }

        .ai-landing__container {
          width: 100%;
          max-width: 666.5px;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          text-align: center;
          margin-inline: auto;
          overflow: visible;
          box-sizing: border-box;
        }

        .ai-landing:not(.ai-landing--chatting) .ai-landing__container {
          gap: 20px;
        }

        .ai-landing--chatting .ai-landing__container {
          max-width: none;
          margin-inline: 0;
        }

        .ai-landing__header {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ai-landing__welcome {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: "Lato", sans-serif;
          font-weight: 600;
          font-size: 15px;
          line-height: normal;
          color: var(--AloOrange, #f96d01);
          text-align: center;
        }

        .ai-landing__welcome img {
          width: 24px;
          height: 24px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .ai-landing__title {
          font-family: "Lato", sans-serif;
          font-weight: 600;
          font-size: 32px;
          line-height: normal;
          color: #000000;
          margin: 0;
        }

        .ai-landing__chips-wrap {
          width: 100%;
          flex-shrink: 0;
        }

        .ai-landing__chips {
          width: 100%;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          overflow-x: auto;
          flex-wrap: nowrap;
          padding-bottom: 4px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .ai-landing__chips::-webkit-scrollbar {
          display: none;
        }

        .ai-landing__chip {
          appearance: none;
          -webkit-appearance: none;
          box-sizing: border-box;
          width: 215px;
          height: 64px;
          min-height: 64px;
          max-height: 64px;
          border: 1px solid #ececec;
          background: rgba(255, 255, 255, 0.55);
          border-radius: 14px;
          padding: 12px;
          cursor: pointer;
          text-align: left;
          overflow: hidden;
          display: block;
          flex: 0 0 auto;
          font-family: "Lato", sans-serif;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          color: #3570d2;
        }

        .ai-landing__chip-label {
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
          min-width: 0;
          max-height: 40px;
          line-height: 20px;
          letter-spacing: 0;
        }

        .ai-landing__prompt-slot {
          width: 100%;
          display: flex;
          flex-direction: column;
        }

        .ai-landing__prompt-slot .ai-prompt-stack {
          width: 100%;
          max-width: 666.5px;
          margin-inline: auto;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          overflow: visible;
        }

        .ai-landing--chatting .ai-landing__prompt-slot .ai-prompt-stack {
          max-width: none;
          margin-inline: 0;
        }

        @media (max-width: 720px) {
          .ai-landing__chips {
            flex-direction: column;
            flex-wrap: nowrap;
            overflow-x: visible;
          }
          .ai-landing__chip {
            width: 100%;
          }
          .ai-landing__title {
            font-size: 26px;
            line-height: 32px;
          }
        }
      </style>

      <iron-ajax
        id="chatHistoryAjax"
        method="GET"
        url="/api/general/ai/chats"
        handle-as="json"
        on-response="_onChatHistoryResponse"
        on-error="_onChatHistoryError"
      ></iron-ajax>

      <iron-ajax
        id="landingAjax"
        method="GET"
        url="/api/general/ai/landing"
        handle-as="json"
        on-response="_onLandingResponse"
        on-error="_onLandingError"
      ></iron-ajax>

      <iron-ajax
        id="chatSyncAjax"
        method="GET"
        url="/api/general/ai/sync"
        handle-as="json"
        on-response="_onChatSyncResponse"
        on-error="_onChatSyncError"
      ></iron-ajax>

      <div class="ai-page__shell">
        <topic-sidebar
          class="ai-page__sidebar"
          is-login="[[isLogin]]"
          user-uid="[[userUid]]"
          selected-topic-id="[[activeSidebarTopicId]]"
          on-click-topic-selected="_onClickTopicSelected"
          on-click-new-topic="_onClickNewTopic"
          on-active-topic-deleted="_onActiveTopicDeleted"
        ></topic-sidebar>
        <div class="ai-page__main">
          <div class$="[[_landingRootClass(showLandingBranding)]]">
            <div class="ai-landing__container">
              <general-toast
                is-active="{{isAttachmentToastActive}}"
                body="[[attachmentToastBody]]"
                color="red"
                container-style="top: 56px; position: fixed; right: auto; margin: 0; box-sizing: border-box; display: flex; justify-content: center; width: min(666.5px, calc(100vw - 338px - 64px)); left: calc(338px + 32px + (100vw - 338px - 64px - min(666.5px, 100vw - 338px - 64px)) / 2);"
              ></general-toast>

              <general-toast
                is-active="{{isSubmitErrorToastActive}}"
                body="[[submitErrorToastBody]]"
                color="red"
                content-style="width: 420px; padding: 12px;"
                body-style="white-space: normal; word-wrap: break-word; overflow-wrap: break-word; text-align: left;"
                container-style="top: 146px; position: fixed; right: auto; margin: 0; box-sizing: border-box; display: flex; left: 660px;"
              ></general-toast>

              <template is="dom-if" if="[[showLandingBranding]]">
                <div class="ai-landing__header">
                  <div class="ai-landing__welcome">
                    <template is="dom-if" if="[[welcomeSubtitleIcon]]">
                      <img src="[[welcomeSubtitleIcon]]" alt="" aria-hidden="true" />
                    </template>
                    <span>[[welcomeSubtitleText]]</span>
                  </div>
                  <h1 class="ai-landing__title">[[welcomeTitle]]</h1>
                </div>

                <template is="dom-if" if="[[!isShowLimitedAccessComposer]]">
                  <div class="ai-landing__chips-wrap">
                    <div id="chipRow" class="ai-landing__chips" role="list">
                      <template is="dom-repeat" items="[[suggestionPromptItems]]">
                        <button
                          type="button"
                          class="ai-landing__chip"
                          role="listitem"
                          on-click="_pickSuggestionPrompt"
                          data-simple$="[[item.simple]]"
                          data-full$="[[item.full]]"
                        >
                          <span class="ai-landing__chip-label">[[item.simple]]</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </template>
              </template>

              <div class="ai-landing__prompt-slot">
                <div class$="[[_promptStackClass(showLandingBranding)]]">
                  <div id="aiPromptStackScroll" class="ai-prompt-stack__scroll">
                    <template is="dom-if" if="[[historyLoading]]">
                      <loading-display></loading-display>
                    </template>

                    <template is="dom-if" if="[[_showTranscriptBody(chatTurns, loadingOnSubmit, historyLoading)]]">
                      <div id="aiTranscriptRoot" class="ai-transcript">
                        <template is="dom-if" if="[[_isShowOlderHistory(chatTurns, hasMoreOlder, historyLoading, loadingOnSubmit)]]">
                          <div
                            id="aiTranscriptOlderSentinel"
                            class="ai-transcript__older-sentinel"
                            aria-hidden="true"
                          ></div>
                        </template>
                        <template is="dom-if" if="[[olderHistoryLoading]]">
                          <div class="ai-transcript__older-loading" aria-busy="true" aria-label="Memuat pesan lama">
                            <div class="ai-transcript__older-spinner" aria-hidden="true"></div>
                            <span class="ai-transcript__older-loading-text">Memuat pesan lama…</span>
                          </div>
                        </template>

                        <template is="dom-repeat" items="[[chatTurns]]" as="turn">
                          <chat-bubble turn="[[turn]]" data-turn-id$="[[turn.id]]"></chat-bubble>
                        </template>

                        <template is="dom-if" if="[[showAnswerSkeleton]]">
                          <div class="ai-transcript__loading-block">
                            <div class="ai-transcript__loading-head">
                              <img src="{{iconStars}}" alt="" aria-hidden="true" />
                              <p class="ai-transcript__loading-copy">[[waitingResponseChatStatusText]]</p>
                            </div>
                            <div class="ai-transcript__skel-rows">
                              <div class="ai-transcript__skel-line">
                                <div class="ai-transcript__skel-pill"></div>
                                <div class="ai-transcript__skel-pill ai-transcript__skel-pill--fixed148"></div>
                                <div class="ai-transcript__skel-pill"></div>
                              </div>
                              <div class="ai-transcript__skel-line">
                                <div class="ai-transcript__skel-pill ai-transcript__skel-pill--fixed215"></div>
                                <div class="ai-transcript__skel-pill ai-transcript__skel-pill--fixed239"></div>
                                <div class="ai-transcript__skel-pill"></div>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </template>
                  </div>

                  <div id="aiPromptStackComposer" class="ai-prompt-stack__composer">
                    <div class="ai-composer">
                      <template is="dom-if" if="[[isErrorDisplayActive]]">
                        <error-display message="[[errorDisplayMessage]]" on-ai-retry="_retry"></error-display>
                      </template>

                      <template is="dom-if" if="[[isShowLimitedAccessComposer]]">
                        <limited-access-composer
                          chat-credit="[[landingData.chat_credit]]"
                          ai-general-copytext="[[landingData.ai_general_copytext]]"
                          on-limited-access-cta-click="_openUnlimitedAccessPopup"
                        ></limited-access-composer>
                      </template>

                      <template is="dom-if" if="[[showLandingBranding]]">
                        <template is="dom-if" if="[[!isShowLimitedAccessComposer]]">
                          <prompt-input-box
                            id="promptBox"
                            is-login="[[isLogin]]"
                            user-uid="[[userUid]]"
                            topic-id="[[topicId]]"
                            user-approve-status="[[userApproveStatus]]"
                            ai-general-copytext="[[landingData.ai_general_copytext]]"
                            placeholder-text="[[landingData.placeholder_text]]"
                            waiting-placeholder-text="[[waitingPlaceholderText]]"
                            loading="[[loadingOnSubmit]]"
                            had-assistant-reply="[[hadAssistantReplySession]]"
                            is-landing="[[showLandingBranding]]"
                            show-attachment-button="[[showAttachmentButton]]"
                            topic-id="[[topicId]]"
                            on-ai-submit="_submit"
                            on-ai-submit-success="_onSubmitQuestionSuccess"
                            on-ai-submit-error="_onSubmitQuestionError"
                            on-attachment-error="_onAttachmentError"
                            max-size-attachment-ai="[[maxSizeAttachmentAi]]"
                            is-should-resume-submit-after-login="[[isShouldResumeSubmitAfterLogin]]"
                      ></prompt-input-box>
                        </template>
                      </template>

                      <template is="dom-if" if="[[!showLandingBranding]]">
                        <template is="dom-if" if="[[isShowLimitUsageBanner]]">
                          <div class="ai-banner-container">
                            <limit-usage-banner
                              chat-credit="[[landingData.chat_credit]]"
                              on-limit-usage-cta-click="_openAddLimitModal"
                            ></limit-usage-banner>
                            <div class="ai-banner-container__input">
                              <prompt-input-box
                                id="promptBox"
                                is-login="[[isLogin]]"
                                user-uid="[[userUid]]"
                                topic-id="[[topicId]]"
                                user-approve-status="[[userApproveStatus]]"
                                ai-general-copytext="[[landingData.ai_general_copytext]]"
                                placeholder-text="[[landingData.placeholder_text]]"
                                waiting-placeholder-text="[[waitingPlaceholderText]]"
                                loading="[[loadingOnSubmit]]"
                                had-assistant-reply="[[hadAssistantReplySession]]"
                                show-attachment-button="[[showAttachmentButton]]"
                                on-ai-submit="_submit"
                                on-ai-submit-success="_onSubmitQuestionSuccess"
                                on-ai-submit-error="_onSubmitQuestionError"
                                on-attachment-error="_onAttachmentError"
                                max-size-attachment-ai="[[maxSizeAttachmentAi]]"
                              ></prompt-input-box>
                            </div>
                          </div>
                        </template>
                      </template>

                      <template is="dom-if" if="[[!showLandingBranding]]">
                        <template is="dom-if" if="[[!isShowLimitUsageBanner]]">
                          <template is="dom-if" if="[[!isShowLimitedAccessComposer]]">
                            <prompt-input-box
                              id="promptBox"
                              is-login="[[isLogin]]"
                              user-uid="[[userUid]]"
                              topic-id="[[topicId]]"
                              user-approve-status="[[userApproveStatus]]"
                              ai-general-copytext="[[landingData.ai_general_copytext]]"
                              placeholder-text="[[landingData.placeholder_text]]"
                              waiting-placeholder-text="[[waitingPlaceholderText]]"
                              loading="[[loadingOnSubmit]]"
                              had-assistant-reply="[[hadAssistantReplySession]]"
                              show-attachment-button="[[showAttachmentButton]]"
                              on-ai-submit="_submit"
                              on-ai-submit-success="_onSubmitQuestionSuccess"
                              on-ai-submit-error="_onSubmitQuestionError"
                              on-attachment-error="_onAttachmentError"
                              max-size-attachment-ai="[[maxSizeAttachmentAi]]"
                            ></prompt-input-box>
                          </template>
                        </template>
                      </template>

                      <template is="dom-if" if="[[!isShowLimitedAccessComposer]]">
                        <p class="ai-composer-disclaimer">[[landingData.footer_text]]</p>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <limit-reached-modal
          id="limitReachedModal"
          ai-general-copytext="[[landingData.ai_general_copytext]]"
          is-credits-exhausted="[[isShowLimitedAccessComposer]]"
        ></limit-reached-modal>

        <add-limit-modal
          id="addLimitModal"
          ai-general-copytext="[[landingData.ai_general_copytext]]"
        ></add-limit-modal>
      </div>
    `;
  }

  static get properties() {
    return {
      isLogin: {
        type: Number,
        value: 0,
        reflectToAttribute: true,
      },
      userUid: {
        type: String,
        value: "",
        reflectToAttribute: true,
      },
      userApproveStatus: {
        type: String,
        value: "",
        reflectToAttribute: true
      },
      topicId: {
        type: String,
        value: "",
        reflectToAttribute: true,
      },
      newTopicId: {
        type: String,
        value: "",
      },
      isNewTopicIdUsed: {
        type: Boolean,
        value: false,
      },
      activeSidebarTopicId: { type: String, value: "" },
      loadingOnSubmit: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
      },
      keepBottomSpace: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
      },
      historyLoading: { type: Boolean, value: false },
      olderHistoryLoading: { type: Boolean, value: false },
      hasMoreOlder: { type: Boolean, value: false },
      landingData: {
        type: Object,
      },
      showAttachmentButton: {
        type: Boolean,
        computed: "_computeShowAttachmentButton(landingData)",
      },
      chatTurns: {
        type: Array,
        value() { return []; },
      },
      showLandingBranding: {
        type: Boolean,
        computed: "_computeShowLandingBranding(chatTurns, loadingOnSubmit, historyLoading)",
      },
      waitingResponseChatStatusText: {
        type: String,
        computed: "_computeWaitingResponseChatStatusText(landingData)",
      },
      waitingPlaceholderText: {
        type: String,
        computed: "_computeWaitingPlaceholderText(landingData)",
      },
      showAnswerSkeleton: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
      },
      // Sticky per page session: true once any non-empty assistant reply has appeared.
      hadAssistantReplySession: {
        type: Boolean,
        value: false,
      },
      iconStars: {
        type: String,
        value: iconStars,
      },
      welcomeSubtitleIcon: {
        type: String,
        computed: "_computeWelcomeSubtitleIcon(landingData)",
      },
      welcomeSubtitleText: {
        type: String,
        computed: "_computeWelcomeSubtitleText(landingData)",
      },
      welcomeTitle: {
        type: String,
        computed: "_computeWelcomeTitle(landingData)",
      },
      suggestionPromptItems: {
        type: Array,
        computed: "_computeSuggestionPromptItems(landingData)",
      },
      isAttachmentToastActive: {
        type: Boolean,
        value: false,
      },
      attachmentToastBody: {
        type: String,
        value: "",
      },
      isSubmitErrorToastActive: {
        type: Boolean,
        value: false,
      },
      submitErrorToastBody: {
        type: String,
        value: "",
      },
      isErrorDisplayActive: {
        type: Boolean,
        value: false,
      },
      errorDisplayMessage: {
        type: String,
        value: "",
      },
      _chatHistoryAppend: {
        type: Boolean,
        value: false,
      },
      _chatHistoryLastChatId: {
        type: String,
        value: "",
      },
      _chatHistoryRetryPending: {
        type: Boolean,
        value: false,
      },
      isShowLimitUsageBanner: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        computed: "_computeIsShowLimitUsageBanner(landingData)",
      },
      isShowLimitedAccessComposer: {
        type: Boolean,
        value: false,
        computed: "_computeIsShowLimitedAccessComposer(landingData)",
      },
      maxSizeAttachmentAi: {
        type: Number
      },
      // Optional origin for SSE (e.g. direct-to-origin host to bypass CDN). Empty = same origin.
      sseBaseUrl: {
        type: String,
        value: "",
        reflectToAttribute: true,
      },
    };
  }

  static get observers() {
    return [];
  }

  // Lifecycle hook for AIPage initialization.
  ready() {
    super.ready();
    this._olderChatsScrollWatcher = null;
    this._chatHistoryScrollHeightBeforePrepend = 0;
    this._answerEventSource = null;
    this._answerStreamTopicId = "";
    this._isMainLandingFetch = false;
    this._isWaitingForNewTopicLanding = false;
    this._isAnswerStreamCancelled = false;
    this._isNetworkOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;
    this._wasStreamInterruptedByOffline = false;
    this._chatSyncPollTimer = null;
    this._isChatSyncPolling = false;
    this._shouldStartChatSyncPollOnStreamOpen = false;
    this._isFetchingLastChatIdForSync = false;
    this._isResumeSubmitAfterLogin = false;
    this._transcriptScrollTop = 0;
    this._transcriptBottomSpacePx = 0;
    this._isProgrammaticTranscriptScroll = false;
    this._programmaticTranscriptScrollTimer = null;
    this._onTranscriptScrollBound = () => this._onTranscriptScroll();
    this._onNetworkOfflineBound = () => this._onNetworkOffline();
    this._onNetworkOnlineBound = () => this._onNetworkOnline();
    window.addEventListener("offline", this._onNetworkOfflineBound);
    window.addEventListener("online", this._onNetworkOnlineBound);
    this._browserBackForwardListener = () => this._onBrowserBackForward();
    window.addEventListener("popstate", this._browserBackForwardListener);
    this._selectSidebarTopicFromUrl();
    this._loadChatHistoryIfTopicInUrl();
    this._saveNewTopicIdIfEmpty(this._readTopicIdFromLandingData(this.landingData));
    if (!this.newTopicId && this.topicId) {
      this._saveNewTopicIdIfEmpty(this.topicId);
    }
    afterNextRender(this, () => {
      this._setupComposerHeightObserver();
      this._bindTranscriptScrollListener();
    });
  }

  // Cleanup scroll watcher for older-chat loading.
  disconnectedCallback() {
    super.disconnectedCallback();
    this._clearNetworkInterruptFlags();
    this._stopChatSyncPoll();
    this._closeAnswerEventSource();
    if (this._onNetworkOfflineBound) {
      window.removeEventListener("offline", this._onNetworkOfflineBound);
      this._onNetworkOfflineBound = null;
    }
    if (this._onNetworkOnlineBound) {
      window.removeEventListener("online", this._onNetworkOnlineBound);
      this._onNetworkOnlineBound = null;
    }
    if (this._browserBackForwardListener) {
      window.removeEventListener("popstate", this._browserBackForwardListener);
      this._browserBackForwardListener = null;
    }
    if (this._olderChatsScrollWatcher) {
      this._olderChatsScrollWatcher.disconnect();
      this._olderChatsScrollWatcher = null;
    }
    if (this._composerHeightObserver) {
      this._composerHeightObserver.disconnect();
      this._composerHeightObserver = null;
    }
    this._unbindTranscriptScrollListener();
    if (this._programmaticTranscriptScrollTimer) {
      clearTimeout(this._programmaticTranscriptScrollTimer);
      this._programmaticTranscriptScrollTimer = null;
    }
  }

  // Keep scroll area and bottom padding in sync with the fixed composer height.
  _setupComposerHeightObserver() {
    const composer =
      this.shadowRoot && this.shadowRoot.getElementById("aiPromptStackComposer");
    if (!composer) return;

    if (this._composerHeightObserver) {
      this._composerHeightObserver.disconnect();
    }

    const applyComposerHeight = () => {
      const DEFAULT_COMPOSER_HEIGHT_PX = 190;
      const measuredHeight = Math.ceil(composer.getBoundingClientRect().height);
      // Prefer measured size, but never reserve less than the default bottom space.
      const composerHeight = Math.max(measuredHeight, DEFAULT_COMPOSER_HEIGHT_PX);
      this.style.setProperty("--ai-composer-height", `${composerHeight}px`);
    };

    this._composerHeightObserver = new ResizeObserver(applyComposerHeight);
    this._composerHeightObserver.observe(composer);
    applyComposerHeight();
  }

  // Reset flags used when the answer stream was cut off by going offline.
  _clearNetworkInterruptFlags() {
    this._wasStreamInterruptedByOffline = false;
  }

  // Read topic_id from a landing payload object.
  _readTopicIdFromLandingData(landingData) {
    if (!landingData || landingData.topic_id == null) return "";
    return String(landingData.topic_id).trim();
  }

  // Store draft topic id from main /ai landing only while newTopicId is still empty.
  _saveNewTopicIdIfEmpty(topicIdFromLanding) {
    const topicId = topicIdFromLanding != null ? String(topicIdFromLanding).trim() : "";
    if (!topicId || this.newTopicId) return;

    this.newTopicId = topicId;
    this.isNewTopicIdUsed = false;
  }

  // Replace draft topic id when main /ai landing returns a fresh id after the draft was used.
  _applyNewTopicIdFromMainLanding(data, { shouldReplace = false } = {}) {
    const freshTopicId = this._readTopicIdFromLandingData(data);
    if (!freshTopicId) return;

    if (shouldReplace || !this.newTopicId) {
      this.newTopicId = freshTopicId;
      this.isNewTopicIdUsed = false;
    }
  }

  // Mark draft topic as used after a successful submit on that draft.
  _markNewTopicIdUsedAfterSubmit(responseBody) {
    const draftTopicId = this.newTopicId ? String(this.newTopicId).trim() : "";
    if (!draftTopicId) return;

    const activeTopicId = this._getActiveTopicId();
    const submitData = responseBody && responseBody.data;
    const topicIdFromSubmit =
      submitData && submitData.topic_id != null
        ? String(submitData.topic_id).trim()
        : "";

    const isSubmitOnDraftTopic =
      activeTopicId === draftTopicId ||
      topicIdFromSubmit === draftTopicId;

    const isFirstSubmitFromEmptyLanding =
      !activeTopicId && Boolean(topicIdFromSubmit);

    const submittedOnDraft = isSubmitOnDraftTopic || isFirstSubmitFromEmptyLanding;

    if (submittedOnDraft) {
      this.isNewTopicIdUsed = true;
    }
  }

  // Close SSE and stop loading UI as soon as the user goes offline.
  _onNetworkOffline() {
    this._isNetworkOffline = true;

    if (!this._answerEventSource && !this.loadingOnSubmit) return;

    this._wasStreamInterruptedByOffline = true;
  }

  // Reconnect SSE when internet is back after an offline interrupt in the same page session.
  _onNetworkOnline() {
    this._isNetworkOffline = false;

    const wasStreamInterruptedByOffline = this._wasStreamInterruptedByOffline;
    this._clearNetworkInterruptFlags();

    if (!wasStreamInterruptedByOffline) return;
    if (this._isAnswerStreamCancelled) return;

    const topicId = this._getActiveTopicId();
    if (!topicId) return;

    this.loadingOnSubmit = true;
    this._clearTranscriptBottomSpace();
    this._syncAnswerSkeleton();
    this._shouldStartChatSyncPollOnStreamOpen = true;
    this._openAnswerStreamIfNeeded(topicId);
  }

  // --- Private: landing data parsers ---

  // Extract subtitle icon URL from landing data's welcome_message_subtitle object.
  // Returns String — icon URL or empty string if not available.
  // Feature: AI Landing — displays subtitle icon next to welcome message
  _computeWelcomeSubtitleIcon(landingData) {
    const subtitleObject = landingData && landingData.welcome_message_subtitle;
    if (subtitleObject && typeof subtitleObject === "object" && subtitleObject.icon_url) return String(subtitleObject.icon_url);
    return "";
  }

  // Extract subtitle text from landing data's welcome_message_subtitle object.
  // Returns String — subtitle text or empty string if not available.
  // Feature: AI Landing — displays subtitle text below welcome message
  _computeWelcomeSubtitleText(landingData) {
    const subtitleObject = landingData && landingData.welcome_message_subtitle;
    if (subtitleObject && typeof subtitleObject === "object" && subtitleObject.text != null) return String(subtitleObject.text);
    return "";
  }

  // Extract the main welcome message title from landing data.
  // Returns String — welcome title or empty string if not available.
  // Feature: AI Landing — displays main welcome heading
  _computeWelcomeTitle(landingData) {
    if (landingData && landingData.welcome_message != null) return String(landingData.welcome_message);
    return "";
  }

  // Parse suggestion_prompts array from landing data into suggestion prompt items (capped at MAX_CHIP_COUNT).
  // Returns Array of { simple: String, full: String } — full falls back to simple if not provided.
  // Feature: AI Landing — renders suggestion prompts below welcome message
  _computeSuggestionPromptItems(landingData) {
    const rawPrompts = (landingData && landingData.suggestion_prompts) || [];
    if (!Array.isArray(rawPrompts)) return [];

    const suggestionPromptItems = rawPrompts.map((entry) => {
      if (entry && typeof entry === "object") {
        const simple = entry.simple_prompt != null ? String(entry.simple_prompt) : "";
        const full =
          entry.full_prompt != null && String(entry.full_prompt).trim() !== ""
            ? String(entry.full_prompt)
            : simple;
        return { simple, full };
      }
      const labelText = String(entry);
      return { simple: labelText, full: labelText };
    });

    const MAX_CHIP_COUNT = 3;

    return suggestionPromptItems.slice(0, MAX_CHIP_COUNT);
  }

  // Extract the first waiting_messages entry as loading status text.
  // Returns String — first waiting message or DEFAULT_WAITING_STATUS_TEXT fallback.
  // Feature: Chat Streaming — shows status text while waiting for AI response
  _computeWaitingResponseChatStatusText(landingData) {
    const waitingMessages = landingData && landingData.waiting_messages;
    if (Array.isArray(waitingMessages) && waitingMessages.length > 0 && waitingMessages[0]) {
      return String(waitingMessages[0]);
    }
    return 'Memahami pertanyaan Anda...';
  }

  // Extract the waiting_placeholder_text for the prompt box busy state.
  // Returns String — placeholder text or DEFAULT_WAITING_PLACEHOLDER_TEXT fallback.
  // Feature: Chat Streaming — shows placeholder in prompt box during loading
  _computeWaitingPlaceholderText(landingData) {
    const placeholder = landingData && landingData.waiting_placeholder_text;
    return placeholder != null && placeholder !== "" ? String(placeholder) : 'Sedang menjawab..';
  }

  // Read attachment feature flag from landing data (parameterize).
  _computeShowAttachmentButton(landingData) {
    return !!(landingData && landingData.can_upload_attachment);
  }

  _computeIsShowLimitUsageBanner(landingData) {
    return parseChatCreditLimit(landingData).isShowLimitUsageBanner;
  }

  _computeIsShowLimitedAccessComposer(landingData) {
    return parseChatCreditLimit(landingData).isShowLimitedAccessComposer;
  }

  // Open the unlimited-access popup when the user taps the upgrade button.
  _openUnlimitedAccessPopup() {
    const modal = this.$.limitReachedModal;
    if (modal && typeof modal.open === "function") modal.open();
  }

  // Open the add-limit popup when the user taps Tambah Limit on the usage banner.
  _openAddLimitModal() {
    const modal = this.$.addLimitModal;
    if (modal && typeof modal.open === "function") modal.open();
  }

  // Show file attachment error from the input box at the top of the screen.
  _onAttachmentError(event) {
    const message =
      event && event.detail && event.detail.message != null
        ? String(event.detail.message)
        : "";
    this.attachmentToastBody = message;
    this.isAttachmentToastActive = true;
  }

  // Show submit question error as toast at the top of the screen.
  _onSubmitQuestionError(event) {
    this._isAnswerStreamCancelled = false;
    this._clearNetworkInterruptFlags();
    this._closeAnswerEventSource();
    const message =
      event && event.detail && event.detail.message != null
        ? String(event.detail.message)
        : "Terjadi kendala. Silakan coba lagi.";

    this.loadingOnSubmit = false;
    this.showAnswerSkeleton = false;
    this._clearTranscriptBottomSpace();

    const errorType =
      event && event.detail && event.detail.type != null
        ? String(event.detail.type)
        : "general";

    this._failAnswerStream(message, errorType);
  }

  // Clear error display and retry history load or resubmit the last failed question.
  _retry() {
    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";

    if (this._chatHistoryRetryPending) {
      this._chatHistoryRetryPending = false;
      this._fetchChatHistory();
      return;
    }

    this._retryFailedSubmit();
  }

  // Resend the last question from transcript without adding a new bubble.
  _retryFailedSubmit() {
    const turns = this.chatTurns || [];
    const lastQuestion = [...turns]
      .reverse()
      .find((turn) => turn && turn.type === "question");
    const promptText =
      lastQuestion && lastQuestion.text != null
        ? String(lastQuestion.text).trim()
        : "";
    if (!promptText) return;

    if (!this.isLogin) {
      this._openLoginOverlay();
      return;
    }

    this._isAnswerStreamCancelled = false;
    this._clearNetworkInterruptFlags();
    this._closeAnswerEventSource();
    this.loadingOnSubmit = true;
    this.showAnswerSkeleton = true;
    this._clearTranscriptBottomSpace();
    this._openAnswerStreamIfNeeded(this._getActiveTopicId());

    const promptBox = this._findPromptBox();
    if (!promptBox || typeof promptBox.resubmitQuestion !== "function") {
      this.loadingOnSubmit = false;
      this.showAnswerSkeleton = false;
      this._clearTranscriptBottomSpace();
      return;
    }

    promptBox.resubmitQuestion(promptText);
  }

  // Return to landing when the user deletes the currently open topic.
  _onActiveTopicDeleted() {
    this.isAttachmentToastActive = false;
    this.attachmentToastBody = "";
    this._resetLanding({ shouldUpdateUrl: true, shouldFetchLanding: true });
  }

  // Start a new topic: fetch fresh landing data and return to the landing screen.
  _onClickNewTopic() {
    this._resetLanding({ shouldUpdateUrl: true, shouldFetchLanding: true });
  }

  // --- Private: navigation handlers ---

  // Read topic id from the current browser path.
  _readTopicIdFromUrl() {
    if (typeof window === "undefined") return null;
    const TOPIC_URL_PATTERN = /^\/ai\/topic\/([^/]+)\/?$/;
    const path = window.location.pathname || "";
    const match = path.match(TOPIC_URL_PATTERN);
    if (!match) return null;
    return match[1];
  }

  // Return true when the path is the AI landing page.
  _isOnLandingPage() {
    if (typeof window === "undefined") return false;
    const path = window.location.pathname || "";
    return path === AI_LANDING_PATH || path === `${AI_LANDING_PATH}/`;
  }

  // Update the address bar to a topic URL without reloading the page.
  _updateUrlToTopic(topicId) {
    if (typeof history === "undefined" || !history.pushState) return;
    history.pushState(
      { aiTopicId: topicId },
      "",
      `/ai/topic/${topicId}`
    );
  }

  // Update the address bar to the landing URL without reloading the page.
  _updateUrlToLanding() {
    if (typeof history === "undefined" || !history.pushState) return;
    history.pushState({ aiTopicId: null }, "", AI_LANDING_PATH);
  }

  // Sync sidebar highlight with the current URL path.
  _selectSidebarTopicFromUrl() {
    if (this._isOnLandingPage()) {
      this.activeSidebarTopicId = "";
      return;
    }

    const topicIdFromUrl = this._readTopicIdFromUrl();
    if (topicIdFromUrl) {
      this.activeSidebarTopicId = topicIdFromUrl;
    }
  }

  // Load chat history when the page opens on a topic URL.
  _loadChatHistoryIfTopicInUrl() {
    const topicIdFromUrl = this._readTopicIdFromUrl();
    if (!topicIdFromUrl) return;

    // check if the user is logged in
    if (!this.isLogin) {
      this._openLoginOverlay();
      return;
    }

    this.topicId = String(topicIdFromUrl);
    this.activeSidebarTopicId = this.topicId;
    this.setAttribute("topic-id", this.topicId);
    this._fetchChatHistory();
  }

  // React to browser back/forward and match URL to chat or landing state.
  _onBrowserBackForward() {
    const topicIdFromUrl = this._readTopicIdFromUrl();

    if (this._isOnLandingPage() || !topicIdFromUrl) {
      this._resetLanding();
      this._selectSidebarTopicFromUrl();
      return;
    }

    if (String(topicIdFromUrl) === String(this.topicId)) {
      this._selectSidebarTopicFromUrl();
      return;
    }

    this.showAnswerSkeleton = false;
    this._clearTranscriptBottomSpace();
    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";
    this._clearNetworkInterruptFlags();
    this._closeAnswerEventSource();
    this.topicId = String(topicIdFromUrl);
    this.activeSidebarTopicId = this.topicId;
    this.setAttribute("topic-id", this.topicId);
    this._fetchLandingData(this.topicId);
    this._fetchChatHistory();
    this._selectSidebarTopicFromUrl();
  }

  // Handle topic selection from the sidebar.
  _onClickTopicSelected(event, isShouldFetchLandingAndChatHistory = true) {
    const topicId = event && event.detail && event.detail.topicId;
    if (!topicId) return;

    this._clearNetworkInterruptFlags();
    this._closeAnswerEventSource();

    // remove answer skeleton when topic is selected
    this.showAnswerSkeleton = false;
    if (isShouldFetchLandingAndChatHistory) {
      this._clearTranscriptBottomSpace();
    }

    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";

    // clear the prompt box value and attachments
    const promptBox = this._findPromptBox();

    if (promptBox) {
      if (typeof promptBox.setValue === "function") promptBox.setValue("");
      if (typeof promptBox.clearAttachments === "function") promptBox.clearAttachments();
    }

    // update topic id
    this.topicId = String(topicId);
    this.activeSidebarTopicId = this.topicId;
    this.setAttribute("topic-id", this.topicId);
    this._updateUrlToTopic(this.topicId);

    if (this._isResumeSubmitAfterLogin) {
      this._isResumeSubmitAfterLogin = false;
      return;
    }

    if (isShouldFetchLandingAndChatHistory) {
      this._fetchLandingData(this.topicId);
      this._fetchChatHistory();
    }
  }

  // --- Private: chat submission ---

  // Start submit: open SSE before POST when topic is known so question events are not missed.
  _submit(event) {
    const promptText =
      (event && event.detail && event.detail.value
        ? String(event.detail.value)
        : ""
      ).trim();
    const attachmentIds = (
      event && event.detail && event.detail.attachmentIds
        ? event.detail.attachmentIds
        : []
    ).filter(Boolean);

    if (!promptText && attachmentIds.length === 0) return;

    if (!this.isLogin) {
      this._openLoginOverlay();
      return;
    }

    this._isResumeSubmitAfterLogin = !!(
      event && event.detail && event.detail.isResumeAfterLogin
    );

    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";
    this._isAnswerStreamCancelled = false;
    this._clearNetworkInterruptFlags();
    this._closeAnswerEventSource();
    this.loadingOnSubmit = true;

    // add the question turn to the chat turns
    const questionTurn = {
      id: PENDING_QUESTION_TURN_ID,
      type: "question",
      text: "",
      imageUrls: [],
      fileNames: [],
      answerId: PENDING_ANSWER_TURN_ID,
    };
    const answerTurn = {
      id: PENDING_ANSWER_TURN_ID,
      type: "answer",
      text: "",
      html: "",
      questionId: PENDING_QUESTION_TURN_ID,
    };
    this.chatTurns = [...(this.chatTurns || []), questionTurn, answerTurn];
    this.showAnswerSkeleton = true;
    this._clearTranscriptBottomSpace();
    afterNextRender(this, () => {
      this._scrollTranscriptToBottom({ scrollToLatestQuestion: true });
    });

    // clear the prompt box value and attachments
    const promptBox = this._findPromptBox();
    if (promptBox && typeof promptBox.setValue === "function") promptBox.setValue("");
    if (promptBox && typeof promptBox.clearAttachments === "function") {
      promptBox.clearAttachments();
    }

    this._openAnswerStreamIfNeeded(this._getActiveTopicId());
  }

  // Open SSE after submit succeeds; reconnect only when topic was not ready before POST.
  _onSubmitQuestionSuccess(event) {
    if (this._isAnswerStreamCancelled) {
      this._isAnswerStreamCancelled = false;
      return;
    }

    const responseBody = event && event.detail && event.detail.responseBody;

    if (!responseBody || responseBody.status !== 200) {
      this._onSubmitQuestionError({ detail: { message: responseBody?.message } });
      return;
    }

    // set the hadAssistantReplySession to true
    this.hadAssistantReplySession = true;

    // mark the new topic id used after submit
    this._markNewTopicIdUsedAfterSubmit(responseBody);

    // set the hadAssistantReplySession to true
    this.hadAssistantReplySession = true;

     this.landingData = syncChatCredit(this.landingData, {
      decreaseBy: CHAT_CREDIT_DECREASE_AFTER_SUBMIT,
    });
    
    this._syncAnswerSkeleton();

    const activeTopicId = this._getActiveTopicId();

    if (!activeTopicId) {

      if (this._isResumeSubmitAfterLogin) {
        this._isResumeSubmitAfterLogin = false;
        return;
      }
      this._fetchLandingData();
      return;
    }

    this._syncActiveTopicId(activeTopicId);
    this._openAnswerStreamIfNeeded(activeTopicId);
  }

  // Return the active conversation topic id from page state.
  _getActiveTopicId() {
    return this.topicId ? String(this.topicId).trim() : "";
  }

  // Keep page topic state and URL aligned with the active topic id.
  _syncActiveTopicId(activeTopicId) {
    if (!activeTopicId) return;

    const previousTopicId = this._getActiveTopicId();
    this.topicId = activeTopicId;
    this.setAttribute("topic-id", activeTopicId);

    if (!previousTopicId || previousTopicId !== activeTopicId || this._isOnLandingPage()) {
      this._updateUrlToTopic(activeTopicId);
    }
  }

  // Open SSE unless the same topic stream is already connected.
  _openAnswerStreamIfNeeded(topicId) {
    const streamTopicId = topicId != null ? String(topicId).trim() : "";
    if (!streamTopicId) return;

    if (this._answerEventSource && this._answerStreamTopicId === streamTopicId) {
      this._startChatSyncPollAfterStreamConnect();
      return;
    }

    this._openAnswerStream(streamTopicId);
  }

  // Close any open EventSource for the answer stream.
  _closeAnswerEventSource() {
    if (!this._answerEventSource) return;

    this._answerEventSource.close();
    this._answerEventSource = null;
    this._answerStreamTopicId = "";
  }

  // Read an error message from a proxy SSE payload shaped as { event: "error", data: {...} }.
  _getProxyStreamErrorMessage(responseStream) {
    if (!responseStream || responseStream.event !== "error") return "";

    const errorData =
      responseStream.data && typeof responseStream.data === "object"
        ? responseStream.data
        : null;
    if (errorData && errorData.message != null && String(errorData.message).trim()) {
      return String(errorData.message);
    }

    return DEFAULT_STREAM_ERROR_MESSAGE;
  }

  // End the stream with an error message shown in the composer.
  _failAnswerStream(message, type = 'general') {
    this._stopChatSyncPoll();
    this._closeAnswerEventSource();
    this.loadingOnSubmit = false;
    this.showAnswerSkeleton = false;
    this._clearTranscriptBottomSpace();

    switch(type){
      case 'general':
        this.errorDisplayMessage = message || DEFAULT_STREAM_ERROR_MESSAGE;
        this.isErrorDisplayActive = true;
        break;
      case 'user_approved_status':
        this.submitErrorToastBody = message;
        this.isSubmitErrorToastActive = true;
    }
  }

  // Return true when the response stream includes a stream_summary message.
  _isResponseStreamSummary(responseStream) {
    if (!responseStream || !Array.isArray(responseStream.messages)) return false;

    return responseStream.messages.some(
      (item) => item && item.event === "stream_summary"
    );
  }

  // Collect appendable text from response stream messages (all message events except stream_summary).
  _getResponseStreamChunkText(responseStream) {
    if (!responseStream || !Array.isArray(responseStream.messages)) return "";

    return responseStream.messages
      .filter(
        (item) =>
          item &&
          item.event !== "stream_summary" &&
          item.message != null
      )
      .map((item) => String(item.message))
      .join("");
  }

  // Update the pending question turn with attachment URLs and real ids from the stream.
  _applyResponseStreamQuestion(responseStream) {
    const mappedQuestion = mapApiChatToTurn(responseStream);
    if (!mappedQuestion || mappedQuestion.type !== "question") return;

    const turns = [...(this.chatTurns || [])];
    const questionIndex = turns.findIndex(
      (turn) =>
        turn &&
        turn.type === "question" &&
        String(turn.id) === PENDING_QUESTION_TURN_ID
    );
    if (questionIndex < 0) return;

    const previousQuestion = turns[questionIndex];
    turns[questionIndex] = {
      ...previousQuestion,
      id: mappedQuestion.id,
      imageUrls: mappedQuestion.imageUrls,
      fileNames: mappedQuestion.fileNames,
      answerId: mappedQuestion.answerId,
      text: previousQuestion.text || mappedQuestion.text,
    };

    const answerIndex = turns.findIndex(
      (turn) =>
        turn &&
        turn.type === "answer" &&
        String(turn.id) === PENDING_ANSWER_TURN_ID
    );
    if (answerIndex >= 0) {
      turns[answerIndex] = {
        ...turns[answerIndex],
        id: mappedQuestion.answerId || turns[answerIndex].id,
        questionId: mappedQuestion.id,
      };
    }

    this.chatTurns = turns;
    // Question text arrives here while submit still uses an empty string placeholder.
    afterNextRender(this, () => {
      this._scrollTranscriptToBottom({ scrollToLatestQuestion: true });
    });
  }

  // Append stream chunk text onto the last answer turn, same shape as history chat.
  _appendResponseStreamChunkToAnswer(responseStream, { replaceText = false } = {}) {
    const chunkText = this._getResponseStreamChunkText(responseStream);
    if (!responseStream || !chunkText) return;

    const turns = [...(this.chatTurns || [])];
    const answerId =
      responseStream.id != null ? String(responseStream.id) : "";

    const lastIndex = turns.length - 1;
    let answerIndex =
      lastIndex >= 0 && turns[lastIndex].type === "answer" ? lastIndex : -1;

    if (answerIndex < 0) {
      const lastQuestion = [...turns]
        .reverse()
        .find((turn) => turn && turn.type === "question");
      turns.push({
        id: answerId || PENDING_ANSWER_TURN_ID,
        type: "answer",
        text: "",
        html: "",
        questionId: lastQuestion ? String(lastQuestion.id) : "",
      });
      answerIndex = turns.length - 1;
    }

    const previousAnswer = turns[answerIndex];
    const nextText = replaceText
      ? chunkText
      : `${previousAnswer.text || ""}${chunkText}`;

    turns[answerIndex] = {
      ...previousAnswer,
      id: answerId || previousAnswer.id,
      type: "answer",
      text: nextText,
      html: convertMarkdownToHtml(nextText),
      questionId:
        responseStream.question_id != null
          ? String(responseStream.question_id)
          : previousAnswer.questionId,
    };
    this.chatTurns = turns;
    this.showAnswerSkeleton = false;
  }

  // Close SSE and end the submit loading state after the stream finishes.
  _finishAnswerStream() {
    this._stopChatSyncPoll();
    this._closeAnswerEventSource();
    // Keep bottom padding when the answer is short so the layout does not collapse.
    this._applyKeptTranscriptBottomSpaceIfNeeded();
    this.loadingOnSubmit = false;
    this.showAnswerSkeleton = false;

    const sidebar = this.shadowRoot && this.shadowRoot.querySelector("topic-sidebar");
    if (sidebar && typeof sidebar.refreshTopics === "function") sidebar.refreshTopics();

    this._onClickTopicSelected({
      detail: {
        topicId: this.topicId,
      },
    }, false)
  }

  // Listen to SSE; append each chunk event, close only on stream_summary.
  _openAnswerStream(topicId) {
    this._closeAnswerEventSource();

    const streamUrl = buildAiChatSseUrl(this.sseBaseUrl, topicId, this.userUid);

    const eventSource = new EventSource(streamUrl);
    this._answerEventSource = eventSource;
    this._answerStreamTopicId = String(topicId);

    eventSource.onopen = () => {
      this._startChatSyncPollAfterStreamConnect();
    };

    // listen to the response stream messages
    eventSource.onmessage = (messageEvent) => {
      let responseStream;
      // parse the response stream message
      try {
        responseStream = JSON.parse(messageEvent.data);
      } catch (parseError) {
        this._failAnswerStream(DEFAULT_STREAM_ERROR_MESSAGE);
        return;
      }

      const IGNORED_SSE_JSON_EVENTS = new Set([
        "proxy_connected",
        "connection",
        "auth_success",
      ]);

      if (responseStream.event && IGNORED_SSE_JSON_EVENTS.has(responseStream.event)) return;

      const proxyErrorMessage = this._getProxyStreamErrorMessage(responseStream);
      if (proxyErrorMessage) {
        this._failAnswerStream(proxyErrorMessage);
        return;
      }

      if (responseStream.type === "question") {
        this._applyResponseStreamQuestion(responseStream);
        return;
      }

      if (this._isChatSyncPolling) {
        if (responseStream.type === "answer") {
          this._appendResponseStreamChunkToAnswer(responseStream, { replaceText: true });
          this._finishAnswerStream();
        }
        return;
      }

      this._appendResponseStreamChunkToAnswer(responseStream, { replaceText: false });

      if (this._isResponseStreamSummary(responseStream)) {
        this._finishAnswerStream();
      }
    };

    // Show an error when the stream closes before stream_summary while still loading.
    eventSource.onerror = () => {
      if (this._wasStreamInterruptedByOffline || this._isNetworkOffline) return;
      if (eventSource.readyState !== EventSource.CLOSED) return;
      if (!this.loadingOnSubmit) return;

      this._failAnswerStream(DEFAULT_STREAM_ERROR_MESSAGE);
    };
  }

  // Start chat sync polling after SSE is open (offline reconnect only).
  _startChatSyncPollAfterStreamConnect() {
    if (!this._shouldStartChatSyncPollOnStreamOpen) return;

    const eventSource = this._answerEventSource;
    const isStreamReady =
      !eventSource || eventSource.readyState === EventSource.OPEN;
    if (!isStreamReady) return;

    this._shouldStartChatSyncPollOnStreamOpen = false;
    this._startChatSyncPoll();
  }

  // Poll GET /ai/chat/sync until stream_summary is returned.
  _startChatSyncPoll() {
    this._stopChatSyncPoll();
    this._isChatSyncPolling = true;
    this._fetchLastChatIdForSync();
  }

  // Fetch last_chat_id from GET /ai/chats before starting chat sync polls.
  _fetchLastChatIdForSync() {
    if (!this._isChatSyncPolling || this._isNetworkOffline) return;

    const topicId = this._getActiveTopicId();
    if (!this.userUid || !topicId) {
      this._scheduleNextChatSyncPoll();
      return;
    }

    this._isFetchingLastChatIdForSync = true;
    this._chatHistoryAppend = false;
    this.$.chatHistoryAjax.params = {
      user_uid: this.userUid,
      topic_id: topicId,
    };
    this.$.chatHistoryAjax.generateRequest();
  }

  // Request one chat sync poll for the active topic.
  _pollChatSyncOnce() {
    if (!this._isChatSyncPolling || this._isNetworkOffline) return;

    const topicId = this._getActiveTopicId();
    if (!this.userUid || !topicId) {
      this._scheduleNextChatSyncPoll();
      return;
    }

    const params = {
      user_uid: this.userUid,
      topic_id: topicId,
    };
    if (this._chatHistoryLastChatId) {
      params.last_chat_id = this._chatHistoryLastChatId;
    }

    this.$.chatSyncAjax.params = params;
    this.$.chatSyncAjax.generateRequest();
  }

  // Queue the next chat sync poll after a short delay.
  _scheduleNextChatSyncPoll() {
    if (!this._isChatSyncPolling) return;

    if (this._chatSyncPollTimer) {
      clearTimeout(this._chatSyncPollTimer);
    }

    const CHAT_SYNC_POLL_INTERVAL_MS = 5000;

    this._chatSyncPollTimer = setTimeout(() => {
      this._chatSyncPollTimer = null;
      this._pollChatSyncOnce();
    }, CHAT_SYNC_POLL_INTERVAL_MS);
  }

  // Stop chat sync polling and clear reconnect poll flags.
  _stopChatSyncPoll() {
    if (this._chatSyncPollTimer) {
      clearTimeout(this._chatSyncPollTimer);
      this._chatSyncPollTimer = null;
    }
    this._isChatSyncPolling = false;
    this._shouldStartChatSyncPollOnStreamOpen = false;
    this._isFetchingLastChatIdForSync = false;
  }

  // Handle chat sync ack from GET /ai/chat/sync; answer content comes from SSE.
  _onChatSyncResponse(event) {
    if (!this._isChatSyncPolling) return;

    const responseBody = event.detail.response;
    const CHATS_SYNC_SUCCESS_HTTP_STATUS = 200;
    if (!responseBody || responseBody.status !== CHATS_SYNC_SUCCESS_HTTP_STATUS) {
      this._scheduleNextChatSyncPoll();
      return;
    }

    if (!this.loadingOnSubmit) {
      this._stopChatSyncPoll();
      return;
    }

    this._scheduleNextChatSyncPoll();
  }

  // Retry chat sync polling after a request failure.
  _onChatSyncError() {
    if (!this._isChatSyncPolling) return;
    this._scheduleNextChatSyncPoll();
  }

  // --- Private: template helpers ---

  // Show the older-history sentinel only when idle (not while submitting/streaming).
  _isShowOlderHistory(turns, hasMoreOlder, historyLoading, loadingOnSubmit) {
    if (historyLoading || loadingOnSubmit) return false;
    return !!(hasMoreOlder && turns && turns.length > 0);
  }

  // Determine whether transcript area should be visible.
  _showTranscriptBody(turns, loadingOnSubmit, historyLoading) {
    if (historyLoading) return false;
    return (turns && turns.length > 0) || !!loadingOnSubmit;
  }

  // Toggle CSS class between landing mode (centered) and chat mode (full-width with fixed composer).
  _promptStackClass(showBranding) {
    return showBranding ? "ai-prompt-stack" : "ai-prompt-stack ai-prompt-stack--chatting";
  }

  // --- Private: computed properties ---

  // Show landing branding (welcome + suggestion prompts) only when no chat is active.
  _computeShowLandingBranding(turns, loadingOnSubmit, historyLoading) {
    if (historyLoading) return false;
    if ((turns && turns.length > 0) || loadingOnSubmit) return false;
    return true;
  }

  // --- Private: data loading ---

  // Fetch landing data from GET /api/general/ai/landing.
  _fetchLandingData(topicId) {
    this.$.landingAjax.params = {
      user_uid: this.userUid,
      topic_id: topicId,
    };    
    this._isMainLandingFetch = !topicId;

    this.$.landingAjax.generateRequest();
  }

  // Apply landing payload; main /ai updates newTopicId, topic landing does not overwrite it.
  _applyLandingFromResponse(data) {
    if (!data || typeof data !== "object") return;

    const isMainLanding = Boolean(this._isMainLandingFetch);
    const shouldRefreshNewTopicId = isMainLanding && this._isWaitingForNewTopicLanding;

    this.landingData = syncChatCredit(data);

    if (isMainLanding) {
      if (shouldRefreshNewTopicId) {
        this._applyNewTopicIdFromMainLanding(data, { shouldReplace: true });
      } else {
        this._saveNewTopicIdIfEmpty(this._readTopicIdFromLandingData(data));
      }
    }

    if (this._isWaitingForNewTopicLanding) {
      this._isWaitingForNewTopicLanding = false;
      if (this.newTopicId) {
        this.topicId = String(this.newTopicId);
        this.setAttribute("topic-id", this.topicId);
      }
    }
  }

  // Handle landing response from GET /api/general/ai/landing.
  _onLandingResponse(event) {
    const responseBody = event.detail.response;

    if (!responseBody || responseBody.status !== 200) {
      this._onLandingError(responseBody && responseBody.message);
      return;
    }
 
    // get the landing data
    const landingData = responseBody.data || {};
    const topicIdFromLanding = this._readTopicIdFromLandingData(landingData);

    // apply the landing data to the component
    this._applyLandingFromResponse(landingData);

    if (!this.loadingOnSubmit) return;

    const activeTopicId = this._getActiveTopicId() || topicIdFromLanding;
    if (!activeTopicId) return;

    this._syncActiveTopicId(activeTopicId);
    this._openAnswerStreamIfNeeded(activeTopicId);
  }

  // Handle landing request failure.
  _onLandingError(message) {
    this.errorDisplayMessage = String(message || "Terjadi kendala. Silakan coba lagi.");
    this.isErrorDisplayActive = true;
  }

  // Fetch chat history for the current topic (initial load or older page).
  _fetchChatHistory() {
    const topicId = this.topicId != null ? String(this.topicId).trim() : "";
    if (!this.userUid || !topicId) return;

    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";
    this._chatHistoryRetryPending = false;
    this.historyLoading = true;
    this.chatTurns = [];
    this.hasMoreOlder = false;
    this._chatHistoryLastChatId = "";
    this._chatHistoryAppend = false;

    if (this._olderChatsScrollWatcher) {
      this._olderChatsScrollWatcher.disconnect();
      this._olderChatsScrollWatcher = null;
    }

    this.$.chatHistoryAjax.params = {
      user_uid: this.userUid,
      topic_id: topicId,
    };
    this.$.chatHistoryAjax.generateRequest();
  }

  // Scroll transcript to the bottom, or align the latest question below the page header.
  _scrollTranscriptToBottom({ scrollToLatestQuestion = false } = {}) {
    const root = this.shadowRoot;
    const scrollEl = this._transcriptScrollElement();
    if (!scrollEl) return;

    if (scrollToLatestQuestion) {
      const turns = this.chatTurns || [];
      const lastQuestion = turns.findLast(
        (turn) => turn && turn.type === "question"
      );
      const questionId =
        lastQuestion && lastQuestion.id != null ? String(lastQuestion.id) : "";
      const questionEl =
        questionId && root.querySelector(`[data-turn-id="${questionId}"]`);
      if (!questionEl) return;

      // Force layout so loading padding-bottom is included in scrollHeight.
      void scrollEl.offsetHeight;

      const nextScrollTop =
        questionEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
      this._markProgrammaticTranscriptScroll();
      scrollEl.scrollTo({
        top: nextScrollTop,
        behavior: "smooth",
      });
      return;
    }

    this._markProgrammaticTranscriptScroll();
    scrollEl.scrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
    this._transcriptScrollTop = scrollEl.scrollTop;
  }

  // Return true when the latest question-to-answer block is shorter than the viewport.
  _shouldKeepTranscriptBottomSpace() {
    const scrollEl = this._transcriptScrollElement();
    const root = this.shadowRoot;
    const transcriptEl = root && root.querySelector("#aiTranscriptRoot");
    if (!scrollEl || !transcriptEl || !root) return false;

    const turns = this.chatTurns || [];
    const lastQuestion = turns.findLast(
      (turn) => turn && turn.type === "question"
    );
    const questionId =
      lastQuestion && lastQuestion.id != null ? String(lastQuestion.id) : "";
    const questionEl =
      questionId && root.querySelector(`[data-turn-id="${questionId}"]`);
    if (!questionEl) return false;

    const paddingBottom =
      parseFloat(getComputedStyle(transcriptEl).paddingBottom) || 0;
    const questionOffsetFromTranscriptTop =
      questionEl.getBoundingClientRect().top -
      transcriptEl.getBoundingClientRect().top;
    const contentBottomWithoutPadding =
      transcriptEl.scrollHeight - paddingBottom;
    const latestBlockHeight =
      contentBottomWithoutPadding - questionOffsetFromTranscriptTop;

    return latestBlockHeight <= scrollEl.clientHeight;
  }

  // Keep the current loading padding as a variable so short answers do not collapse.
  _applyKeptTranscriptBottomSpaceIfNeeded() {
    if (!this._shouldKeepTranscriptBottomSpace()) {
      this._clearTranscriptBottomSpace();
      return;
    }

    const root = this.shadowRoot;
    const transcriptEl = root && root.querySelector("#aiTranscriptRoot");
    const scrollEl = this._transcriptScrollElement();
    if (!transcriptEl || !scrollEl) {
      this._clearTranscriptBottomSpace();
      return;
    }

    const currentPadding =
      parseFloat(getComputedStyle(transcriptEl).paddingBottom) || 0;
    const fullSpacePx =
      currentPadding > 0 ? currentPadding : scrollEl.clientHeight;
    this._setTranscriptBottomSpacePx(fullSpacePx);
  }

  // Update transcript bottom padding in pixels (used after short answers).
  _setTranscriptBottomSpacePx(spacePx) {
    const nextSpacePx = Math.max(0, Math.round(spacePx));
    this._transcriptBottomSpacePx = nextSpacePx;
    this.keepBottomSpace = nextSpacePx > 0;

    const root = this.shadowRoot;
    const transcriptEl = root && root.querySelector("#aiTranscriptRoot");
    if (!transcriptEl) return;

    transcriptEl.style.setProperty(
      "--ai-transcript-bottom-space",
      `${nextSpacePx}px`
    );
  }

  // Clear kept bottom padding on the transcript.
  _clearTranscriptBottomSpace() {
    this._setTranscriptBottomSpacePx(0);
  }

  // Ignore scroll events caused by programmatic scrollTo / scrollTop updates.
  _markProgrammaticTranscriptScroll() {
    const PROGRAMMATIC_TRANSCRIPT_SCROLL_IGNORE_MS = 500;

    this._isProgrammaticTranscriptScroll = true;
    if (this._programmaticTranscriptScrollTimer) {
      clearTimeout(this._programmaticTranscriptScrollTimer);
    }
    this._programmaticTranscriptScrollTimer = setTimeout(() => {
      this._isProgrammaticTranscriptScroll = false;
      this._programmaticTranscriptScrollTimer = null;
      const scrollEl = this._transcriptScrollElement();
      if (scrollEl) {
        this._transcriptScrollTop = scrollEl.scrollTop;
      }
    }, PROGRAMMATIC_TRANSCRIPT_SCROLL_IGNORE_MS);
  }

  // Shrink kept bottom space gradually as the user scrolls up toward older messages.
  _onTranscriptScroll() {
    const scrollEl = this._transcriptScrollElement();
    if (!scrollEl) return;

    const currentScrollTop = scrollEl.scrollTop;
    const previousScrollTop = this._transcriptScrollTop || 0;
    this._transcriptScrollTop = currentScrollTop;

    if (this._isProgrammaticTranscriptScroll) return;

    const scrolledUpPx = previousScrollTop - currentScrollTop;
    if (scrolledUpPx <= 0) return;
    if (!(this._transcriptBottomSpacePx > 0)) return;

    this._setTranscriptBottomSpacePx(
      this._transcriptBottomSpacePx - scrolledUpPx
    );
    this._transcriptScrollTop = scrollEl.scrollTop;
  }

  // Listen for user scroll on the transcript container.
  _bindTranscriptScrollListener() {
    const scrollEl = this._transcriptScrollElement();
    if (!scrollEl || !this._onTranscriptScrollBound) return;

    this._unbindTranscriptScrollListener();
    scrollEl.addEventListener("scroll", this._onTranscriptScrollBound, {
      passive: true,
    });
    this._transcriptScrollTop = scrollEl.scrollTop;
  }

  // Stop listening for transcript scroll events.
  _unbindTranscriptScrollListener() {
    const scrollEl = this._transcriptScrollElement();
    if (!scrollEl || !this._onTranscriptScrollBound) return;

    scrollEl.removeEventListener("scroll", this._onTranscriptScrollBound);
  }

  // Get the transcript scroll element.
  _transcriptScrollElement() {
    const root = this.shadowRoot;
    return root && root.querySelector("#aiPromptStackScroll");
  }

  // Load older chat messages when the user scrolls to the top sentinel.
  _loadOlderChats() {
    if (!this.hasMoreOlder || this.olderHistoryLoading || this.historyLoading || this.loadingOnSubmit) {
      return;
    }

    const topicId = this.topicId != null ? String(this.topicId).trim() : "";
    if (!this.userUid || !topicId || !this._chatHistoryLastChatId) return;

    if (this._olderChatsScrollWatcher) {
      this._olderChatsScrollWatcher.disconnect();
      this._olderChatsScrollWatcher = null;
    }

    this.olderHistoryLoading = true;
    this._chatHistoryAppend = true;

    // Get the scroll height before prepend.
    const scrollElement = this._transcriptScrollElement();
    this._chatHistoryScrollHeightBeforePrepend = scrollElement ? scrollElement.scrollHeight : 0;

    this.$.chatHistoryAjax.params = {
      user_uid: this.userUid,
      topic_id: topicId,
      last_chat_id: this._chatHistoryLastChatId,
    };
    this.$.chatHistoryAjax.generateRequest();
  }

  // Handle chat history response from GET /ai/chats.
  _onChatHistoryResponse(event) {
    const CHATS_SUCCESS_HTTP_STATUS = 200;

    if (this._isFetchingLastChatIdForSync) {
      this._isFetchingLastChatIdForSync = false;

      const responseBody = event.detail.response;
      if (responseBody && responseBody.status === CHATS_SUCCESS_HTTP_STATUS) {
        const data = responseBody.data || {};
        this._chatHistoryLastChatId =
          data.last_chat_id != null ? String(data.last_chat_id) : "";
      }

      if (this._isChatSyncPolling) {
        this._pollChatSyncOnce();
      }
      return;
    }

    const isAppend = this._chatHistoryAppend;

    if (isAppend) {
      this.olderHistoryLoading = false;
    } else {
      this.historyLoading = false;
    }

    const responseBody = event.detail.response;

    if (!responseBody || responseBody.status !== 200) {
      const message = responseBody && responseBody.message;
      this._showChatHistoryError(message, isAppend);
      return;
    }

    const CHAT_HISTORY_PAGE_SIZE = 20;
    const data = responseBody.data || {};
    const chats = Array.isArray(data.chats) ? data.chats : [];
    const turns = chats.map(mapApiChatToTurn).filter(Boolean);
    this._chatHistoryLastChatId = data.last_chat_id != null ? String(data.last_chat_id) : "";
    this.hasMoreOlder =
      chats.length >= CHAT_HISTORY_PAGE_SIZE && !!this._chatHistoryLastChatId;

    if (isAppend) {
      const previousScrollHeight = this._chatHistoryScrollHeightBeforePrepend || 0;
      this._chatHistoryScrollHeightBeforePrepend = 0;

      this.chatTurns = turns.concat(this.chatTurns || []);

      afterNextRender(this, () => {
        const scrollElement = this._transcriptScrollElement();
        if (scrollElement && previousScrollHeight > 0) {
          scrollElement.scrollTop = scrollElement.scrollHeight - previousScrollHeight;
        }
        if (this.hasMoreOlder) {
          this._bindOlderChatsScrollObserver();
        }
      });
      return;
    }

    this.chatTurns = turns;
    this.hadAssistantReplySession = turns.some((turn) => turn.type === "answer");

    afterNextRender(this, () => {
      this._bindOlderChatsScrollObserver();
      this._scrollTranscriptToBottom();
    });
  }

  // Handle chat history request failure.
  _onChatHistoryError() {
    if (this._isFetchingLastChatIdForSync) {
      this._isFetchingLastChatIdForSync = false;
      if (this._isChatSyncPolling) {
        this._pollChatSyncOnce();
      }
      return;
    }

    const isAppend = this._chatHistoryAppend;

    if (isAppend) {
      this.olderHistoryLoading = false;
      this._chatHistoryScrollHeightBeforePrepend = 0;
      afterNextRender(this, () => {
        if (this.hasMoreOlder) {
          this._bindOlderChatsScrollObserver();
        }
      });
      return;
    }

    this.historyLoading = false;
    this.chatTurns = [];
    this._showChatHistoryError();
  }

  // Show error state when chat history cannot be loaded.
  _showChatHistoryError(message, isAppend) {
    if (isAppend) {
      this._chatHistoryScrollHeightBeforePrepend = 0;
      afterNextRender(this, () => {
        if (this.hasMoreOlder) {
          this._bindOlderChatsScrollObserver();
        }
      });
      return;
    }

    this.chatTurns = [];
    this._chatHistoryRetryPending = true;
    this.errorDisplayMessage = String(message || "Terjadi kendala. Silakan coba lagi.");
    this.isErrorDisplayActive = true;
  }

  // Watch the top sentinel and load older chats when it becomes visible.
  _bindOlderChatsScrollObserver() {
    if (!this.hasMoreOlder) return;

    if (this._olderChatsScrollWatcher) {
      this._olderChatsScrollWatcher.disconnect();
      this._olderChatsScrollWatcher = null;
    }

    afterNextRender(this, () => {
      if (!this.hasMoreOlder) return;

      const root = this.shadowRoot;
      const sentinel = root && root.querySelector("#aiTranscriptOlderSentinel");
      const scrollEl = root && root.querySelector("#aiPromptStackScroll");

      if (!sentinel || typeof IntersectionObserver === "undefined") return;

      const scrollRoot = scrollEl || null;

      this._olderChatsScrollWatcher = new IntersectionObserver(
        (entries) => {
          if (!entries[0] || !entries[0].isIntersecting) return;
          if (this.loadingOnSubmit || this.historyLoading || this.olderHistoryLoading) return;
          this._loadOlderChats();
        },
        { root: scrollRoot, threshold: 0 }
      );

      this._olderChatsScrollWatcher.observe(sentinel);
    });
  }

  // --- Private: state management helpers ---

  // Reset entire component state for a new conversation.
  _resetLanding({
    shouldUpdateUrl = false,
    shouldFetchLanding = false,
  } = {}) {
    this.chatTurns = [];
    this.hadAssistantReplySession = false;
    this.activeSidebarTopicId = "";
    this.hasMoreOlder = false;
    this.historyLoading = false;
    this.olderHistoryLoading = false;
    this._chatHistoryLastChatId = "";
    this._chatHistoryAppend = false;
    this._chatHistoryRetryPending = false;
    this.loadingOnSubmit = false;
    this._clearTranscriptBottomSpace();
    this.isErrorDisplayActive = false;
    this.errorDisplayMessage = "";
    this._clearNetworkInterruptFlags();
    this._stopChatSyncPoll();
    this._closeAnswerEventSource();
    // Hide file attachment and submit error toasts when chat state is cleared.
    this.isAttachmentToastActive = false;
    this.attachmentToastBody = "";
    this.isSubmitErrorToastActive = false;
    this.submitErrorToastBody = "";
    if (this._olderChatsScrollWatcher) {
      this._olderChatsScrollWatcher.disconnect();
      this._olderChatsScrollWatcher = null;
    }
    if (shouldUpdateUrl) {
      this._updateUrlToLanding();
    }

    if (shouldFetchLanding) {
      this._isWaitingForNewTopicLanding = true;
      this.topicId = "";
      this.setAttribute("topic-id", "");
      this._fetchLandingData();
      return;
    }

    this.topicId = "";
    this.setAttribute("topic-id", "");
  }

  // Sync the skeleton loading indicator based on current loading state and last turn.
  _syncAnswerSkeleton() {
    const turns = this.chatTurns || [];
    const lastTurn = turns[turns.length - 1];
    this.showAnswerSkeleton = !!(
      this.loadingOnSubmit &&
      lastTurn &&
      lastTurn.type === "answer" &&
      !(lastTurn.text && String(lastTurn.text).trim())
    );
  }

  // Get reference to the prompt-box sub-component.
  _findPromptBox() {
    const root = this.shadowRoot;
    if (!root) return null;
    const boxes = root.querySelectorAll("prompt-input-box");
    for (const box of boxes) {
      if (box.offsetParent !== null) return box;
    }
    return boxes[0] || null;
  }

  // Show the login modal overlay (legacy jQuery entrance-overlay).
  _openLoginOverlay() {
    try {
      if (window.$) window.$("#entrance-overlay").css("display", "block");
    } catch (overlayError) {
      console.error("[_openLoginOverlay] Failed to show login overlay:", overlayError);
    }
  }

  // Fill the prompt box with the full prompt from a picked suggestion prompt.
  _pickSuggestionPrompt(event) {
    const suggestionPromptTarget = event && event.currentTarget;
    const full = (suggestionPromptTarget && suggestionPromptTarget.dataset && suggestionPromptTarget.dataset.full) || "";
    const simple = (suggestionPromptTarget && suggestionPromptTarget.dataset && suggestionPromptTarget.dataset.simple) || "";
    const value = (full && String(full).trim()) || simple;
    const box = this._findPromptBox();
    if (box && typeof box.setValue === "function") box.setValue(value);
    if (box && typeof box.focusTextarea === "function") box.focusTextarea();
  }

  // --- Private: landing helpers ---

  // Build root element CSS class based on branding visibility
  _landingRootClass(showBranding) {
    const base = "ai-landing";
    return showBranding ? base : `${base} ${base}--chatting`;
  }
}

customElements.define("ai-page", AIPage);
